import { Extension } from '@tiptap/core';
import { Plugin } from 'prosemirror-state';

export interface CreatedAtExtensionOptions {
	saveTimestamp: boolean;
}

export const CreatedAtExtension = Extension.create<CreatedAtExtensionOptions>({
	name: 'createdAt',

	addOptions() {
		return {
			saveTimestamp: false,
		};
	},

	addGlobalAttributes() {
		// Only add attributes if timestamp tracking is enabled
		if (!this.options.saveTimestamp) {
			return [];
		}

		return [
			{
				types: ['paragraph', 'heading'],
				attributes: {
					'data-created-at': {
						default: null,
						parseHTML: (el) => el.getAttribute('data-created-at'),
						renderHTML: (attrs) => (attrs['data-created-at'] ? { 'data-created-at': attrs['data-created-at'] } : {}),
					},
				},
			},
		];
	},

	addStorage() {
		return {
			trackedNodeTimestamps: new Set<string>(),
		};
	},

	addProseMirrorPlugins() {
		return [
			new Plugin({
				appendTransaction: (transactions, oldState, newState) => {
					const { saveTimestamp } = this.options;
					if (!saveTimestamp || !transactions.some((tx) => tx.docChanged)) {
						return null;
					}

					const tr = newState.tr;
					let modified = false;

					// Initialize trackedNodeTimestamps if it doesn't exist
					if (!this.editor.storage.trackedNodeTimestamps) {
						this.editor.storage.trackedNodeTimestamps = new Set<string>();
					}
					const trackedNodeTimestamps = this.editor.storage.trackedNodeTimestamps as Set<string>;

					// Track IDs seen in this transaction to detect duplicates from splits
					const idsInThisTransaction = new Set<string>();

					newState.doc.descendants((node, pos) => {
						if (!node.isBlock || !['paragraph', 'heading'].includes(node.type.name)) {
							return true;
						}

						const nodeId = node.attrs['data-node-id'];
						const hasCreatedAt = !!node.attrs['data-created-at'];

						// Only process nodes that have an ID (assigned by CreatedByUserExtension)
						if (!nodeId) {
							return true;
						}

						// Check for duplicate IDs in this transaction (indicates a split)
						const isDuplicateInTransaction = idsInThisTransaction.has(nodeId);

						if (isDuplicateInTransaction) {
							// This is a duplicate ID from a split - assign new timestamp
							const timestamp = new Date().toISOString();
							const newAttrs = {
								...node.attrs,
								'data-created-at': timestamp,
							};
							tr.setNodeMarkup(pos, node.type, newAttrs);
							trackedNodeTimestamps.add(nodeId);
							idsInThisTransaction.add(nodeId);
							modified = true;
						} else {
							// Not a duplicate - add to transaction tracking
							idsInThisTransaction.add(nodeId);

							// Check if this is a node we've never seen before globally
							const isNewNode = !trackedNodeTimestamps.has(nodeId);

							if (isNewNode) {
								// Track this node so we don't process it again
								trackedNodeTimestamps.add(nodeId);

								// Only set timestamp if it doesn't already have one
								if (!hasCreatedAt) {
									const timestamp = new Date().toISOString();
									const newAttrs = {
										...node.attrs,
										'data-created-at': timestamp,
									};
									tr.setNodeMarkup(pos, node.type, newAttrs);
									modified = true;
								}
							}
						}

						return true;
					});

					return modified ? tr : null;
				},
			}),
		];
	},
});
